/**
 * Local cache for search results and other data
 */

import type { CacheEntry } from '../types';

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

class Cache {
  private storage: Map<string, CacheEntry<any>>;

  constructor() {
    this.storage = new Map();
  }

  set<T>(key: string, data: T, ttl: number = DEFAULT_TTL): void {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl,
    };
    this.storage.set(key, entry);
  }

  get<T>(key: string): T | null {
    const entry = this.storage.get(key);
    if (!entry) return null;

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.storage.delete(key);
      return null;
    }

    return entry.data as T;
  }

  has(key: string): boolean {
    const entry = this.storage.get(key);
    if (!entry) return false;

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.storage.delete(key);
      return false;
    }

    return true;
  }

  delete(key: string): void {
    this.storage.delete(key);
  }

  clear(): void {
    this.storage.clear();
  }

  // Cleanup expired entries
  cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.storage.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.storage.delete(key);
      }
    }
  }
}

// Singleton instance
const cache = new Cache();

// Auto-cleanup every minute
if (typeof window !== 'undefined') {
  setInterval(() => cache.cleanup(), 60 * 1000);
}

export { cache };
